/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import dayjs from "dayjs";
import { useMemo } from "react";
import { useHotkeyContext } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import { useScrapEyeMetadata } from "contexts/scrapEyeMetadata";
import { AreaHotkeyMap } from "components/scrapEyeLayout/scrapEyeLayoutCommon";
import { useTenant } from "hooks/settings";
import {
  ScrapLoadingLayoutResponse,
  useGetScrapLoadingLayoutQuery,
} from "src/store/api/generatedApi";

type ScrapLoadingLayout = ScrapLoadingLayoutResponse["layout"];

export const layoutAreaIds = (layout: ScrapLoadingLayout): string[] => {
  switch (layout.layout_type) {
    case "bay":
      return layout.data.columns.flat().filter((areaId) => areaId !== null);
    case "railcar":
      return layout.data.tracks.flatMap((track) =>
        track.areas.map((area) => area.area_id)
      );
    case "grid":
      return layout.data.areas.map((area) => area.area_id);
  }
};

/**
 * The set of areas that exist in the given location's layout (lowercased so a
 * layout that labels an area `ECS` still matches an `ecs` hotkey). Returns null
 * — meaning "don't scope" — when no location is given (e.g. the hotkey-remap
 * modal and guides, which show all areas) or the layout hasn't loaded yet.
 *
 * Multi-crane tenants share one global `area_hotkeys` map in which the same
 * letter maps to one area per crane. The per-location layout is already
 * crane-scoped server-side, so intersecting `area_hotkeys` with it keeps only
 * the current location's areas and resolves the collision at source.
 */
const useLocationAreaIds = (
  location: string | undefined
): ReadonlySet<string> | null => {
  const tenantName = useTenant();
  const startOfDay = dayjs().startOf("day").toISOString();

  const { data } = useGetScrapLoadingLayoutQuery(
    { tenantName, timestamp: startOfDay, location: location ?? "" },
    { skip: location === undefined }
  );

  return useMemo(() => {
    if (location === undefined || data?.layout == null) return null;
    return new Set(layoutAreaIds(data.layout).map((id) => id.toLowerCase()));
  }, [location, data]);
};

const NUMBERED_BIN_PATTERN = /_bin_(\d+)$/;

/**
 * Maps each numbered bin (area ids matching `*_bin_<n>`) in the location's
 * layout to its area id — empty when there's no location, the layout hasn't
 * loaded, or it has no numbered bins.
 */
export const useNumberedBinAreas = (
  location: string | undefined
): ReadonlyMap<number, string> => {
  const tenantName = useTenant();
  const startOfDay = dayjs().startOf("day").toISOString();

  const { data } = useGetScrapLoadingLayoutQuery(
    { tenantName, timestamp: startOfDay, location: location ?? "" },
    { skip: location === undefined }
  );

  return useMemo(() => {
    if (location === undefined || data?.layout == null) {
      return new Map<number, string>();
    }
    return new Map(
      layoutAreaIds(data.layout).flatMap((areaId) => {
        const match = NUMBERED_BIN_PATTERN.exec(areaId);
        return match ? [[Number(match[1]), areaId] as const] : [];
      })
    );
  }, [location, data]);
};

/**
 * Returns a map of area name -> current hotkey (with user overrides applied),
 * scoped to the areas in the current location's layout when a location is
 * given. Falls back to all areas when the location is omitted or its layout is
 * unavailable, so single-crane tenants and the remap/guide views are unaffected.
 */
export const useAreaHotkeys = (location?: string): AreaHotkeyMap => {
  const { getAreaHotkey } = useHotkeyContext();
  const { area_hotkeys: defaultAreaHotkeys } = useScrapEyeMetadata();
  const locationAreaIds = useLocationAreaIds(location);

  const areaHotkeys = useMemo(() => {
    if (!defaultAreaHotkeys) return {};
    return Object.fromEntries(
      Object.entries(defaultAreaHotkeys)
        .filter(
          ([area]) =>
            locationAreaIds === null || locationAreaIds.has(area.toLowerCase())
        )
        .map(([area, defaultKey]) => [area, getAreaHotkey(area, defaultKey)])
    );
  }, [defaultAreaHotkeys, locationAreaIds, getAreaHotkey]);

  return areaHotkeys;
};

/**
 * Returns a reverse mapping: hotkey code (KeyA, Digit1, etc.) -> area name.
 */
export const useHotkeyCodeToAreaMapping = (
  location?: string
): Record<string, string> => {
  const areaHotkeys = useAreaHotkeys(location);

  return useMemo(() => {
    const result: Record<string, string> = {};
    for (const [area, hotkey] of Object.entries(areaHotkeys ?? {})) {
      const key = hotkey.toUpperCase();
      if (key.length === 1) {
        if (/[A-Z]/.test(key)) {
          result[`Key${key}`] = area;
        } else if (/[0-9]/.test(key)) {
          result[`Digit${key}`] = area;
        }
      }
    }
    return result;
  }, [areaHotkeys]);
};

/**
 * Returns a reverse mapping: lowercase hotkey -> area name.
 */
export const useHotkeyToAreaMapping = (
  location?: string
): Record<string, string> => {
  const areaHotkeys = useAreaHotkeys(location);

  return useMemo(() => {
    const result: Record<string, string> = {};
    for (const [area, hotkey] of Object.entries(areaHotkeys ?? {})) {
      result[hotkey.toLowerCase()] = area;
    }
    return result;
  }, [areaHotkeys]);
};
